import type { ReactNode } from 'react';
import { Topbar, BackButton } from '@shared/ui/topbar';
import { Headphones, Menu as MenuIcon, PanelLeft } from 'lucide-react';

import styles from './loading.module.css';

const TOTAL_OF_ITEMS = 20;

/**
 * The real `Topbar` widget renders here, not a skeleton placeholder for it
 * (user directive, 2026-08-26): navigating from `preview` into
 * `/issues/:issueId/:articleId` used to swap the preview page's real navbar
 * for this page's shimmering fake one while the issue loaded, then swap
 * again for the real navbar once it resolved -- two visible swaps for what
 * should read as one continuous navbar. `Topbar`'s own default `left`
 * (`BackButton`, via `useGoBack`) works with no article/issue data at all,
 * so it's real and functional immediately.
 *
 * `left`/`right` also carry disabled placeholders for the sidebar-toggle and
 * menu buttons `responsive.tsx`'s real `Topbar` always renders (regardless
 * of which article loads) -- without these, a cold-cache load showed a
 * *narrower* navbar (just the back button) that then visibly gained two more
 * icons the instant the real reader mounted, a second flash on top of the
 * one Phase 0.14 fixed. Reserving the same slots here, at the same size,
 * means only the buttons' *content* changes (disabled -> functional), not
 * the layout.
 *
 * `hasAudio` is the one piece of that layout genuinely unknowable at the
 * two earlier phases this component also covers (router-pending, and the
 * `!currentArticle` guard in `IssueReaderShell`) -- there's no article yet
 * to check. Left `undefined` (no player placeholder) there, same tradeoff
 * as before. But `IssueReaderShell` also uses this component as its
 * Suspense fallback for `responsive.tsx`'s own chunk load (Phase 0.14),
 * and *that* usage already has `currentArticle` in hand -- passing it
 * through there means the very last swap before the real reader takes
 * over shows the exact same button set either side of it, which is the
 * swap a reader actually perceives as "the page finished loading".
 */
export function Loading({ hasAudio }: LoadingProps = {}) {
	return (
		<div className={styles['loading']}>
			{/* `loading.module.css` positions this via a bare `nav` tag
			    selector (`.loading nav { grid-area: topbar; ... }`), not a
			    class -- no className needed for the grid placement to apply. */}
			<Topbar
				left={
					<>
						<BackButton />
						<PlaceholderButton
							icon={<PanelLeft color="var(--topbar-buttons-stroke)" />}
						/>
					</>
				}
				right={
					<>
						{hasAudio ? (
							<PlaceholderButton
								icon={<Headphones color="var(--topbar-buttons-stroke)" />}
							/>
						) : null}
						<PlaceholderButton
							icon={<MenuIcon color="var(--topbar-buttons-stroke)" />}
						/>
					</>
				}
			/>
			<Sidebar />
			<ArticleContentSkeleton />
		</div>
	);
}

export interface LoadingProps {
	hasAudio?: boolean;
}

function PlaceholderButton({ icon }: { icon: ReactNode }) {
	return (
		<button type="button" disabled aria-hidden="true">
			{icon}
		</button>
	);
}

function Sidebar() {
	return (
		<aside>
			<div>
				<div className={styles['shine']} />
			</div>
			<ul>
				{Array(TOTAL_OF_ITEMS)
					.fill(0)
					.map((_, i) => (
						<Item key={i} />
					))}
			</ul>
		</aside>
	);
}

function Item() {
	return (
		<li>
			<div className={[styles['image'], styles['shine']].join(' ')} />
			<div className={styles['text']}>
				<div className={styles['shine']} />
				<div className={styles['shine']} />
			</div>
		</li>
	);
}

/**
 * The article-text shimmer alone, no topbar/sidebar chrome around it --
 * exported so other single-article pages with their own real topbar (no
 * sidebar concept at all) can reuse just this piece instead of pulling in
 * `Loading`'s full issue-reader layout. First reuse: `pages/favorite/ui/
 * favorite.tsx`, which already renders its own real `Topbar` for the same
 * reason documented on `Loading` above -- swapping a real navbar for a fake
 * one and back reads as two flashes, not one continuous bar.
 */
export function ArticleContentSkeleton() {
	return (
		<section>
			<div className={styles['content']}>
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
			</div>
		</section>
	);
}
